import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../store/selectors/UserSelectors";
import React, { useCallback, useState } from "react";
import { TApiOrder } from "../../../api/endpoints/Order";
import {
  approveOrder,
  cancelOrder,
  duplicateOrder,
  rejectOrder,
  removeOrder
} from "../../../store/action-creators/orders";
import Link from "../../Navigation/Links/Link";
import { paths } from "../../../router";
import { Badge, Dropdown } from "react-bootstrap";
import OrderStatusBadge from "../../Ui/OrderStatusBadge";
import IconButton from "../../Buttons/IconButton";
import { faEllipsisV, faMinusCircle } from "@fortawesome/free-solid-svg-icons";
import OrderPolicy from "../../../policies/OrderPolicy";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClone, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import { useHistory } from "react-router";
import { Nullable } from "../../../../types/utils";
import OrderName from "../../OrderName";
import DateTime from "../../../utils/DateTime";
import ConfirmDialog from "../../ConfrimDialog";

interface IProps {
  order: TApiOrder;
  onOrderChange?: (order: Nullable<TApiOrder>) => void;
}

const OrderRow = (props: IProps) => {
  const { order, onOrderChange } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const [duplicateWarningDialogOpen, setDuplicateWarningDialogOpen] = useState(false);

  const onRemove = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(removeOrder(order.id)).then(() => {
      onOrderChange?.(null);
    });
  }, [dispatch, onOrderChange]);

  const onDuplicate = useCallback((order: TApiOrder) => () => {
    if (order.has_legs) {
      setDuplicateWarningDialogOpen(true);
    } else {
      // @ts-ignore
      dispatch(duplicateOrder(order)).then(order => {
        history.push(paths.order(order.id));
      });
    }
  }, [dispatch, history]);

  const onDuplicateConfirmation = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(duplicateOrder(order)).then(order => {
      history.push(paths.order(order.id));
    });
  }, [dispatch, history]);

  const onCancel = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(cancelOrder(order)).then((result) => {
      onOrderChange?.(result);
    });
  }, [dispatch, onOrderChange]);

  const onApprove = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(approveOrder(order)).then((result) => {
      onOrderChange?.(result);
    });
  }, [dispatch, onOrderChange]);

  const onReject = useCallback((order: TApiOrder) => () => {
    // @ts-ignore
    dispatch(rejectOrder(order)).then((result) => {
      onOrderChange?.(result);
    });
  }, [dispatch, onOrderChange]);

  return (
    <tr className="order-row">
      <td><Link to={paths.order(order.id)}>{order.id}</Link></td>
      <td><OrderName order={order} /></td>
      <td>{order.customer}</td>
      <td><Badge variant="info">{order.category}</Badge></td>
      <td><OrderStatusBadge status={order.status} /></td>
      <td>{DateTime.Format.dateTimeWithTimezone(order.updated)}</td>
      <td className="actions">
        <Dropdown>
          <Dropdown.Toggle
            as={IconButton}
            icon={faEllipsisV}
            title="Actions"
            variant="primary"
            id={`order-actions-dropdown-${order.id}`}
          />

          <Dropdown.Menu>
            {
              OrderPolicy.canApprove(user, order) &&
              <Dropdown.Item as="button" onClick={onApprove(order)} title="Approve order" className="success">
                <FontAwesomeIcon icon={faCheckCircle} />
                Approve order
              </Dropdown.Item>
            }

            {
              OrderPolicy.canReject(user, order) &&
              <Dropdown.Item as="button" onClick={onReject(order)} title="Reject order" className="danger">
                <FontAwesomeIcon icon={faMinusCircle} />
                Reject order
              </Dropdown.Item>
            }

            {
              OrderPolicy.canDuplicate(user, order) &&
              <Dropdown.Item
                as="button"
                onClick={onDuplicate(order)}
                title="Duplicate order"
              >
                <FontAwesomeIcon icon={faClone} />
                Duplicate order
              </Dropdown.Item>
            }

            {
              OrderPolicy.canRemove(user, order) &&
              <Dropdown.Item as="button" onClick={onRemove(order)} title="Remove order" className="danger">
                <FontAwesomeIcon icon={faTrashAlt} />
                Remove order
              </Dropdown.Item>
            }

            {
              OrderPolicy.canCancel(user, order) &&
              <Dropdown.Item as="button" onClick={onCancel(order)} title="Cancel order" className="danger">
                <FontAwesomeIcon icon={faMinusCircle} />
                Cancel order
              </Dropdown.Item>
            }
          </Dropdown.Menu>
        </Dropdown>

        <ConfirmDialog
          open={duplicateWarningDialogOpen}
          onClose={() => setDuplicateWarningDialogOpen(false)}
          onConfirm={onDuplicateConfirmation(order)}
          cancelText="Cancel"
          confirmText="Proceed"
          message="Please be aware that the quote you want to duplicate contains products using Legs (ENS extended products used to have Leg 1 and Leg 2 time steps), which are no longer available in the new 48r1 cycle. Previous quotes with Legs will be automatically updated to match the new 48r1 cycle requirements."
        />
      </td>
    </tr>
  )
}

export default OrderRow;
